/**
 * VM-14b — Buchung erfassen (Soll/Ist).
 * Extrahiert aus TenancyDetail.jsx.
 */
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { POSTING_CATEGORY_GROUPS } from "@/lib/constants";
import { F } from "./TenancyBits";

export default function PostingDialog({ tenancyId, onSaved }) {
  const [form, setForm] = useState({
    posting_date: new Date().toISOString().slice(0, 10),
    // iter156 — Bei manueller Ist-Buchung ist Default „_auto" (FIFO-Aufteilung
    // wie bei Bankmatch), damit eine Sammelzahlung sofort auf Kaltmiete/NK/HK/
    // Stellplatz nach Sollstellung verteilt wird. Bei „Soll" fällt der Nutzer
    // automatisch auf `rent_cold` zurück.
    type: "ist", category: "_auto", amount: 0, reference: "",
  });
  const set = (k, v) => setForm((s) => {
    const next = { ...s, [k]: v };
    // Bei Wechsel des Typs sinnvollen Kategorie-Default setzen
    if (k === "type") {
      if (v === "ist" && (s.category === "rent_cold" || !s.category)) next.category = "_auto";
      if (v === "soll" && s.category === "_auto") next.category = "rent_cold";
    }
    // 2026-02 · Bei negativem Betrag (Rücklastschrift/Rückerstattung) ist
    // die Auto-Aufteilung nicht sinnvoll — sonst würde der Betrag im Waterfall
    // fälschlich als positive Zahlung gebucht. Deshalb automatisch auf
    // `rent_cold` (bzw. `refund`) umschalten und dem Nutzer Feedback geben.
    if (k === "amount" && parseFloat(v) < 0 && next.category === "_auto") {
      next.category = "rent_cold";
    }
    return next;
  });
  const isNegative = parseFloat(form.amount) < 0;
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/tenancies/${tenancyId}/postings`, {
        ...form, tenancy_id: tenancyId, amount: Number(form.amount) || 0,
      });
      const replaced = (data && data.replaced_auto_settlement_ids) || [];
      const split = (data && data.split_postings) || [];
      if (split.length > 1) {
        toast.success(
          `Zahlung von ${Number(form.amount).toLocaleString("de-DE", { minimumFractionDigits: 2 })} € auf ${split.length} Sollpositionen aufgeteilt.`,
          { duration: 6000 },
        );
      } else if (replaced.length > 0) {
        toast.warning(
          `${replaced.length} automatisch generierte Ausgleichsbuchung${replaced.length === 1 ? "" : "en"} wurde${replaced.length === 1 ? "" : "n"} durch diese echte Zahlung ersetzt.`,
          { duration: 7000 },
        );
      } else {
        toast.success("Buchung erfasst");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>Buchung erfassen</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <F label="Datum"><Input type="date" required value={form.posting_date} onChange={(e) => set("posting_date", e.target.value)} /></F>
          <F label="Typ">
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="soll">Soll (Forderung)</SelectItem>
                <SelectItem value="ist">Haben (Zahlung)</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Kategorie">
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="posting-category-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-80">
                {/* iter156 — Auto-Split nur bei Ist/Haben-Buchungen & positivem Betrag */}
                {form.type === "ist" && !isNegative && (
                  <div>
                    <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-emerald-700">
                      Empfohlen
                    </div>
                    <SelectItem value="_auto" className="pl-4" data-testid="posting-cat-auto">
                      🔀 Automatisch aufteilen (FIFO nach Sollstellungen)
                    </SelectItem>
                  </div>
                )}
                {POSTING_CATEGORY_GROUPS.map((g) => (
                  <div key={g.key}>
                    <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                      {g.label}
                    </div>
                    {g.items.map((it) => (
                      <SelectItem key={it.value} value={it.value} className="pl-4">
                        {it.label}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
            {form.category === "_auto" && form.type === "ist" && (
              <p className="text-[11px] text-emerald-700 mt-1">
                Zahlung wird gegen offene Sollstellungen verrechnet (Kaltmiete → Nebenkosten → Heizkosten → Stellplatz, Ältestes zuerst).
              </p>
            )}
            {isNegative && (
              <p className="text-[11px] text-amber-700 mt-1">
                Negativer Betrag erkannt (Rücklastschrift / Rückerstattung) — bitte konkrete Kategorie wählen. Auto-Aufteilung ist deaktiviert.
              </p>
            )}
          </F>
          <F label="Betrag €"><Input type="number" step="0.01" required value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="posting-amount" /></F>
          <F label="Referenz / Buchungstext" className="col-span-2"><Input value={form.reference} onChange={(e) => set("reference", e.target.value)} /></F>
        </div>
        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="posting-save-btn">Erfassen</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
